
export const formatNumberForDisplay = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (str === "") return "";
  return str.replace(".", ",");
};

export const formatNumberForStorage = (value: string): string => {
  return value.replace(",", ".");
};

export const handleNumericInput = (
  value: string,
  allowDecimals: boolean = true
): { displayValue: string; storageValue: string } => {
  let cleaned = value.replace(/[^\d,.-]/g, "");

  cleaned = cleaned.replace(/-/g, "");

  const hasComma = cleaned.includes(",");
  const hasPeriod = cleaned.includes(".");
  
  if (hasComma && hasPeriod) {
    const lastComma = cleaned.lastIndexOf(",");
    const lastPeriod = cleaned.lastIndexOf(".");
    if (lastComma > lastPeriod) {
      cleaned = cleaned.replace(/\./g, "");
    } else {
      cleaned = cleaned.replace(/,/g, "");
    }
  }
  
  cleaned = cleaned.replace(",", ".");

  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }

  if (!allowDecimals) {
    cleaned = cleaned.split(".")[0];
  }

  if (allowDecimals && cleaned.includes(".")) {
    const [integer, decimal] = cleaned.split(".");
    cleaned = integer + "." + decimal.slice(0, 2);
  }

  const displayValue = cleaned.replace(".", ",");
  const storageValue = cleaned;

  return { displayValue, storageValue };
};

export const validateNonNegative = (value: number | string): boolean => {
  const num = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
  return !isNaN(num) && num >= 0;
};

/**
 * Masks free-typed input for a percentage field. Keeps a single decimal separator
 * (rendered as a comma, matching formatNumberForDisplay) and caps the fraction at two
 * digits. A trailing separator survives so "12," is still typeable on the way to "12,5".
 * The magnitude is unbounded unless a caller passes `max`.
 */
export const maskPercentageInput = (raw: string, max?: number): string => {
  // Accepts pasted values like "12.5%", "12,5 %" or "R$ 12" and keeps only the number.
  let cleaned = String(raw ?? "").replace(/[^\d.,]/g, "").replace(/\./g, ",");
  if (cleaned === "") return "";

  // Only the first separator is a decimal point; later ones are dropped rather than
  // being read as thousands separators (a percentage never needs them).
  const firstSeparator = cleaned.indexOf(",");
  if (firstSeparator !== -1) {
    cleaned =
      cleaned.slice(0, firstSeparator + 1) +
      cleaned.slice(firstSeparator + 1).replace(/,/g, "");
  }

  const hasSeparator = cleaned.includes(",");
  const [rawInteger, rawDecimal] = cleaned.split(",");

  // "007" collapses to "7", but the leading zero in "0,5" is meaningful and stays.
  let integer = rawInteger.replace(/^0+(?=\d)/, "");
  if (integer === "" && !hasSeparator) return "";
  if (integer === "") integer = "0";

  const decimal = rawDecimal === undefined ? undefined : rawDecimal.slice(0, 2);
  const masked = hasSeparator ? `${integer},${decimal ?? ""}` : integer;

  if (max !== undefined) {
    const numeric = Number(masked.replace(",", "."));
    if (Number.isFinite(numeric) && numeric > max) return String(max);
  }

  return masked;
};

/** Reads the numeric value out of a percentage field masked by maskPercentageInput. */
export const parsePercentageInput = (display: string | null | undefined): number => {
  const parsed = Number(String(display ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Renders a stored percentage back into the masked display form. Decimals only show
 * when they carry information: 12 -> "12", 12.5 -> "12,5", 12.25 -> "12,25".
 */
export const formatPercentageForDisplay = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(String(value).replace(",", "."));
  if (!Number.isFinite(numeric) || numeric === 0) return "";
  return String(Math.round(numeric * 100) / 100).replace(".", ",");
};
